import {
  AdminGetUserLoyaltyEventsResponse,
  AdminGetUserResponse,
  GetUserLoyaltyPointsResponse,
  GetUserPermissionsResponse,
  GetUserSegmentCampaignAudienceCountRequest,
  GetUserSegmentCampaignAudienceCountResponse,
  LoyaltyRewardDto,
  UserRolesDto,
  UsersApiClient,
} from '@kotipizzagroup/kotipizza-users-api-client';
import { getConfiguration } from '../shared/utils/configuration';
import Axios from 'axios';
import { getRequestHeaders } from './utils';
import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';

let client: UsersApiClient | null = null;

const getApiClient = (): UsersApiClient => {
  if (client) return client;

  const baseUrl = getConfiguration<string>('usersApi.root', '');

  // Keep axios's default JSON parsing (no `transformResponse: []`): users-api-client doesn't parse
  // response bodies itself.
  const instance = Axios.create({
    timeout: 10000,
  });

  instance.interceptors.request.use(async (config) => {
    const headers = await getRequestHeaders();
    config.headers = headers;
    return config;
  });

  client = new UsersApiClient(baseUrl, instance);
  return client;
};

export const getUserByIdOrEmail = async (userIdOrEmail: string): Promise<AdminGetUserResponse | null> => {
  const client = getApiClient();

  if (userIdOrEmail.includes('@')) {
    return await client.adminGetUserByEmail({ email: userIdOrEmail });
  }

  const userId = parseInt(userIdOrEmail, 10);

  if (Number.isInteger(userId)) {
    return await client.adminGetUserById(userId);
  }

  console.error('Search term is not an user id or email');
  return null;
};

export const sendForgotPasswordEmail = async (email: string): Promise<void> => {
  const client = getApiClient();
  await client.forgotPassword({ email: email });
};

export const getUserSegmentCampaignAudienceCount = async (
  getUserSegmentCampaignAudienceCountRequest: GetUserSegmentCampaignAudienceCountRequest
): Promise<GetUserSegmentCampaignAudienceCountResponse> => {
  const client = getApiClient();
  return client.getUserSegmentCampaignAudienceCount(getUserSegmentCampaignAudienceCountRequest);
};

export const useUserSegmentCampaignAudienceCount = (
  getUserSegmentCampaignAudienceCountRequest: GetUserSegmentCampaignAudienceCountRequest
): UseQueryResult<GetUserSegmentCampaignAudienceCountResponse> => {
  const queryEnabled =
    getUserSegmentCampaignAudienceCountRequest.buyingFrequencyClassIds.length > 0 ||
    getUserSegmentCampaignAudienceCountRequest.customerCategoryIds.length > 0;

  return useQuery(
    ['user-segment-campaign-audience-count', getUserSegmentCampaignAudienceCountRequest],
    () => getUserSegmentCampaignAudienceCount(getUserSegmentCampaignAudienceCountRequest),
    {
      enabled: queryEnabled,
      placeholderData: { audienceCount: 0 },
    }
  );
};

export const getUserPermissions = async (): Promise<GetUserPermissionsResponse> => {
  const client = getApiClient();
  return await client.getUserPermissions();
};

export const getUserRoles = async (): Promise<UserRolesDto> => {
  const client = getApiClient();
  return await client.getUserRoles();
};

export const softDeleteUser = async (userId: number): Promise<void> => {
  const client = getApiClient();
  return await client.softDeleteUser(userId);
};

const getUserLoyaltyPoints = async (userId: number): Promise<GetUserLoyaltyPointsResponse> => {
  const client = getApiClient();
  return await client.adminGetUserLoyaltyPoints(userId);
};

export const getUserLoyaltyEvents = async (userId: number): Promise<AdminGetUserLoyaltyEventsResponse> => {
  const client = getApiClient();
  return await client.adminGetUserLoyaltyEvents(userId);
};

export const getLoyaltyRewards = async (): Promise<LoyaltyRewardDto[]> => {
  const client = getApiClient();
  return await client.getLoyaltyRewards();
};

export const useUserLoyaltyPoints = (userId: number): UseQueryResult<GetUserLoyaltyPointsResponse> => {
  return useQuery(['user-loyalty-points', userId], () => getUserLoyaltyPoints(userId), {
    enabled: !!userId,
  });
};

export const useRefreshUserLoyaltyPoints = (userId: number): (() => Promise<void>) => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries(['user-loyalty-points', userId]);
};

export const addUserLoyaltyPoints = async (userId: number, amount: number): Promise<void> => {
  const client = getApiClient();
  return await client.adminAddLoyaltyPointsToUser(userId, { pointsToAdd: amount });
};

export const removeUserLoyaltyPoints = async (userId: number, amount: number): Promise<void> => {
  const client = getApiClient();
  return await client.adminRemoveLoyaltyPointsFromUser(userId, { pointsToRemove: amount });
};
